import {Loader} from 'src/design/atoms/Loader';
import {Spotlight} from '@mantine/spotlight';
import React from 'react';
import type {
  SearchResultItem} from 'src/api/ProactApiClient';
import {
  SearchEntityTypeEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {
  SidebarLinkActions,
  SidebarLinkAssets,
  SidebarLinkForms,
  SidebarLinkPersonnel,
  SidebarLinkSafetyEvents,
  SidebarLinkTraining,
  SidebarLinkVendors,
} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import type {SidebarLinkItemProps} from 'src/app/layout/Sidebar/components/SidebarLinks';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useGlobalSearch} from './useGlobalSearch';
import {useSearchNavActions} from './searchNavActions';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const ENTITY_TYPE_SIDEBAR_LINK: Record<
  SearchEntityTypeEnum,
  SidebarLinkItemProps
> = {
  [SearchEntityTypeEnum.Users]: SidebarLinkPersonnel,
  [SearchEntityTypeEnum.TaskDefinitions]: SidebarLinkActions,
  [SearchEntityTypeEnum.FormDefinitions]: SidebarLinkForms,
  [SearchEntityTypeEnum.Vendors]: SidebarLinkVendors,
  [SearchEntityTypeEnum.CourseDefinitions]: SidebarLinkTraining,
  [SearchEntityTypeEnum.SafetyEvents]: SidebarLinkSafetyEvents,
  [SearchEntityTypeEnum.Assets]: SidebarLinkAssets,
};

const getResultPath = (item: SearchResultItem): string => {
  switch (item.entityType) {
    case SearchEntityTypeEnum.Users:
      return PathUtility.PersonnelView(item.id, 'overview');
    case SearchEntityTypeEnum.TaskDefinitions: {
      // Backend sends the enum's name (e.g. "Approval"), not its numeric value.
      const taskTypeName = item.metadata?.taskType;
      const taskType =
        taskTypeName != null
          ? TaskTypeEnum[taskTypeName as keyof typeof TaskTypeEnum]
          : undefined;
      return taskType != null
        ? PathUtility.TaskView(taskType, item.id, 'details')
        : PathUtility.Actions('manage');
    }
    case SearchEntityTypeEnum.FormDefinitions:
      return PathUtility.FormBuilderView(item.id);
    case SearchEntityTypeEnum.Vendors:
      return PathUtility.VendorsView(item.id, 'overview');
    case SearchEntityTypeEnum.CourseDefinitions:
      return PathUtility.TrainingCourseView(String(item.id), 'build');
    case SearchEntityTypeEnum.SafetyEvents:
      return PathUtility.SafetyEventView(item.id, 'content');
    case SearchEntityTypeEnum.Assets:
      return PathUtility.AssetView(item.id, 'overview');
    default:
      return PathUtility.Dashboard();
  }
};

export const GlobalSearch: React.FC = () => {
  const navigate = useAppNavigate();
  const {query, setQuery, groups, isEligible, isSearching} = useGlobalSearch();
  const navActions = useSearchNavActions();

  return (
    <Spotlight.Root
      query={query}
      onQueryChange={setQuery}
      shortcut={['mod + K']}
      scrollable
      maxHeight={420}>
      <Spotlight.Search
        placeholder="Search personnel, actions, forms, vendors, training, safety events, assets..."
        rightSection={isSearching ? <Loader size="xs" /> : undefined}
      />
      <Spotlight.ActionsList>
        {!isEligible ? (
          navActions.length > 0 ? (
            <Spotlight.ActionsGroup label="Go to">
              {navActions.map(action => (
                <Spotlight.Action
                  key={action.id}
                  label={action.label}
                  leftSection={action.icon}
                  onClick={() => navigate(action.path)}
                />
              ))}
            </Spotlight.ActionsGroup>
          ) : (
            <Spotlight.Empty>Start typing to search...</Spotlight.Empty>
          )
        ) : groups.length === 0 ? (
          <Spotlight.Empty>
            {isSearching ? 'Searching...' : 'No results found'}
          </Spotlight.Empty>
        ) : (
          groups.map(group => {
            const link = ENTITY_TYPE_SIDEBAR_LINK[group.entityType];
            return (
              <Spotlight.ActionsGroup
                key={group.entityType}
                label={link?.name ?? String(group.entityType)}>
                {group.items.map(item => (
                  <Spotlight.Action
                    key={`${item.entityType}-${item.id}`}
                    label={item.title}
                    description={item.subtitle}
                    leftSection={
                      link
                        ? React.cloneElement(link.icon as React.ReactElement, {
                            size: 16,
                          })
                        : undefined
                    }
                    onClick={() => navigate(getResultPath(item))}
                  />
                ))}
              </Spotlight.ActionsGroup>
            );
          })
        )}
      </Spotlight.ActionsList>
    </Spotlight.Root>
  );
};
